import React, { useState } from 'react';
import { Building2, Globe, ArrowUpRight, AlertTriangle, Clock, ArrowUp, ArrowDown, ArrowUpDown, Scale, Search, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { ResourcesHero } from '../components/hero/ResourcesHero';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export function Resources() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'name',
    direction: 'asc'
  });

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  // Learning Resources Section
  const learningResources = [
    {
      name: "Binance Academy",
      description: "Comprehensive crypto education platform by Binance",
      strengths: ["Extensive content library", "Free resources", "Multiple languages"],
      weaknesses: ["Platform-specific content", "Basic tutorials"],
      link: "https://academy.binance.com"
    },
    {
      name: "Coursera",
      description: "University-backed blockchain courses and certifications",
      strengths: ["Academic quality", "Structured learning", "Certificates"],
      weaknesses: ["Paid courses", "Academic focus"],
      link: "https://www.coursera.org/courses?query=blockchain"
    },
    {
      name: "CryptoZombies",
      description: "Interactive blockchain programming tutorials",
      strengths: ["Interactive learning", "Practical exercises", "Free content"],
      weaknesses: ["Limited scope", "Development focus"],
      link: "https://cryptozombies.io"
    },
    {
      name: "Decrypt Learn",
      description: "News-focused crypto education platform",
      strengths: ["Current topics", "Easy to understand", "Regular updates"],
      weaknesses: ["Limited depth", "News-centric"],
      link: "https://decrypt.co/learn"
    },
    {
      name: "MIT OpenCourseWare",
      description: "Academic blockchain and cryptocurrency courses",
      strengths: ["High-quality content", "Comprehensive", "Free access"],
      weaknesses: ["Technical focus", "Self-paced only"],
      link: "https://ocw.mit.edu/search/?q=blockchain"
    }
  ];

  // Security Token Platforms
  const securityTokenPlatforms = [
    {
      name: "Tokeny Solutions",
      description: "Luxembourg-based tokenization platform for financial assets",
      strengths: ["EU Compliant", "Enterprise-grade", "Multi-asset support"],
      weaknesses: ["Higher costs", "Complex integration"],
      link: "https://tokeny.com"
    },
    {
      name: "Polymath",
      description: "Leading security token platform with ST-20 standard",
      strengths: ["ST-20 standard", "Regulatory compliance", "Large ecosystem"],
      weaknesses: ["Complex setup", "Higher costs"],
      link: "https://polymath.network"
    },
    {
      name: "Harbor",
      description: "Institutional-grade security token platform",
      strengths: ["Institutional focus", "Compliance automation", "Strong security"],
      weaknesses: ["Enterprise only", "US market focus"],
      link: "https://harbor.com"
    },
    {
      name: "Securitize",
      description: "Digital asset compliance platform",
      strengths: ["DS Protocol", "Global reach", "Full compliance"],
      weaknesses: ["Premium pricing", "Complex integration"],
      link: "https://securitize.io"
    },
    {
      name: "Swarm",
      description: "Decentralized security token infrastructure",
      strengths: ["Decentralized", "Low fees", "Open source"],
      weaknesses: ["Smaller network", "Technical complexity"],
      link: "https://swarm.com"
    }
  ];

  // Crypto Loan Platforms
  const loanPlatforms = [
    {
      name: "Nexo",
      description: "Leading crypto lending platform with instant loans",
      strengths: ["Instant loans", "Multiple assets", "Insurance coverage"],
      weaknesses: ["Higher interest rates", "Limited availability"],
      link: "https://nexo.io"
    },
    {
      name: "BlockFi",
      description: "Institutional-grade crypto lending platform",
      strengths: ["Institutional backing", "Good rates", "Multiple products"],
      weaknesses: ["Limited asset selection", "Geographic restrictions"],
      link: "https://blockfi.com"
    },
    {
      name: "Celsius",
      description: "Crypto lending platform with high yield rates",
      strengths: ["High yield rates", "No fees", "Weekly rewards"],
      weaknesses: ["Platform risk", "Limited transparency"],
      link: "https://celsius.network"
    },
    {
      name: "Aave",
      description: "Decentralized lending protocol",
      strengths: ["Decentralized", "Flash loans", "Multiple assets"],
      weaknesses: ["Complex interface", "Smart contract risk"],
      link: "https://aave.com"
    },
    {
      name: "Compound",
      description: "Algorithmic money market protocol",
      strengths: ["Automated rates", "Open source", "Governance"],
      weaknesses: ["Limited assets", "Technical barriers"],
      link: "https://compound.finance"
    }
  ];

  // Legal Firms Data
  const legalFirms = [
    {
      name: "Perkins Coie",
      location: "Global",
      description: "Pioneer in blockchain and digital currency legal practice with industry-leading expertise",
      strengths: ["Regulatory compliance", "ICO/STO advisory", "International coverage", "Industry pioneer"],
      weaknesses: ["Premium pricing", "High demand limits availability"],
      link: "https://www.perkinscoie.com/en/practices/fintech-technology/blockchain-digital-currency-and-digital-assets.html"
    },
    {
      name: "Cooley LLP",
      location: "Global",
      description: "Leading technology and fintech law firm with extensive blockchain practice",
      strengths: ["Tech industry expertise", "Startup friendly", "Comprehensive services", "Strong regulatory team"],
      weaknesses: ["Higher cost structure", "US-centric approach"],
      link: "https://www.cooley.com/services/practice/fintech"
    },
    {
      name: "Latham & Watkins",
      location: "Global",
      description: "Top-tier international law firm with dedicated blockchain and cryptocurrency practice",
      strengths: ["Global network", "Deep regulatory expertise", "Full-service coverage", "Strong corporate practice"],
      weaknesses: ["Premium rates", "Large enterprise focus"],
      link: "https://www.lw.com/en/practices/fintech-and-payments"
    },
    {
      name: "Sullivan & Cromwell",
      location: "Global",
      description: "Elite law firm with significant cryptocurrency and blockchain regulatory expertise",
      strengths: ["SEC relationships", "Regulatory influence", "High-profile cases", "International reach"],
      weaknesses: ["Very high rates", "Selective client acceptance"],
      link: "https://www.sullcrom.com/practices-fintech-blockchain-cryptocurrency"
    },
    {
      name: "Skadden",
      location: "Global",
      description: "Leading global law firm with extensive digital asset and blockchain practice",
      strengths: ["Complex regulatory matters", "International expertise", "Strong litigation team", "Innovation focus"],
      weaknesses: ["Premium pricing", "Large client focus"],
      link: "https://www.skadden.com/capabilities/practices/blockchain-cryptocurrency-and-digital-assets"
    },
    {
      name: "DLA Piper",
      location: "Global",
      description: "International law firm with dedicated blockchain and digital assets practice",
      strengths: ["Global presence", "Tech industry focus", "Regulatory expertise", "Startup support"],
      weaknesses: ["Complex engagement process", "Varying expertise by region"],
      link: "https://www.dlapiper.com/en/services/corporate/fintech/blockchain-and-digital-assets"
    },
    {
      name: "Fenwick & West",
      location: "United States",
      description: "Technology-focused law firm with strong digital asset practice",
      strengths: ["Tech industry expertise", "Startup focused", "Innovation leadership", "Practical approach"],
      weaknesses: ["US market focus", "Limited international presence"],
      link: "https://www.fenwick.com/services/practices/blockchain"
    },
    {
      name: "Morrison & Foerster",
      location: "Global",
      description: "International firm with dedicated fintech and blockchain practice",
      strengths: ["Asian market expertise", "Technology focus", "Regulatory knowledge", "Cross-border transactions"],
      weaknesses: ["Higher rates", "Complex structure"],
      link: "https://www.mofo.com/capabilities/blockchain"
    },
    {
      name: "White & Case",
      location: "Global",
      description: "Global law firm with comprehensive digital assets practice",
      strengths: ["International coverage", "Financial regulation expertise", "Strong litigation team", "Cross-border capability"],
      weaknesses: ["Premium pricing", "Large client preference"],
      link: "https://www.whitecase.com/industries/financial-institutions/fintech"
    },
    {
      name: "Goodwin",
      location: "Global",
      description: "Technology-focused firm with strong digital currency practice",
      strengths: ["Fintech expertise", "Startup friendly", "Innovation focus", "Practical solutions"],
      weaknesses: ["US/UK centric", "Growing but limited crypto team"],
      link: "https://www.goodwinlaw.com/services/practices/digital-currency--blockchain-technology"
    },
    {
      name: "K&L Gates",
      location: "Global",
      description: "Global firm with dedicated digital assets and blockchain practice",
      strengths: ["Global reach", "Technical understanding", "Regulatory insight", "Cross-border expertise"],
      weaknesses: ["Complex engagement process", "Variable expertise by office"],
      link: "https://www.klgates.com/Digital-Assets-Blockchain-and-Cryptocurrency"
    },
    {
      name: "Clifford Chance",
      location: "Global",
      description: "Magic Circle firm with strong fintech and digital assets practice",
      strengths: ["European expertise", "Global network", "Regulatory leadership", "Innovation focus"],
      weaknesses: ["Very high rates", "Large client focus"],
      link: "https://www.cliffordchance.com/expertise/services/tech-group/fintech.html"
    },
    {
      name: "Allen & Overy",
      location: "Global",
      description: "Leading international firm with advanced fintech practice",
      strengths: ["European market leader", "Strong regulatory team", "Innovation focus", "Global coverage"],
      weaknesses: ["Premium pricing", "Complex structure"],
      link: "https://www.allenovery.com/en-gb/global/expertise/practices/financial-services-regulatory/financial-services-and-fintech"
    },
    {
      name: "Linklaters",
      location: "Global",
      description: "Global law firm with dedicated fintech and digital assets practice",
      strengths: ["International expertise", "Strong regulatory practice", "Innovation leadership", "Cross-border capability"],
      weaknesses: ["High cost structure", "Large enterprise focus"],
      link: "https://www.linklaters.com/en/client-services/practices/financial-regulation/fintech"
    },
    {
      name: "Hogan Lovells",
      location: "Global",
      description: "International law firm with comprehensive blockchain practice",
      strengths: ["Global coverage", "Regulatory expertise", "Industry connections", "Technical understanding"],
      weaknesses: ["Premium rates", "Complex engagement model"],
      link: "https://www.hoganlovells.com/en/industry/blockchain-and-crypto"
    }
  ];

  // Filter and sort firms
  const filteredFirms = legalFirms.filter(firm =>
    firm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    firm.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    firm.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFirms = [...filteredFirms].sort((a, b) => {
    const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
    return a[sortConfig.key as keyof typeof a]
      .toString()
      .localeCompare(b[sortConfig.key as keyof typeof b].toString()) * multiplier;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <ResourcesHero />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Learning Resources Section */}
        <section className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Learning Resources
              </h2>
              <p className="mt-2 text-gray-600">
                Top educational platforms for cryptocurrency and blockchain learning
              </p>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Strengths
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weaknesses
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {learningResources.map((resource, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <h3 className="text-blue-600 font-medium">{resource.name}</h3>
                          <p className="text-sm text-gray-600">{resource.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {resource.strengths.map((strength, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {resource.weaknesses.map((weakness, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <a
                          href={resource.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Visit
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Security Token Platforms Section */}
        <section className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Security Token Platforms
              </h2>
              <p className="mt-2 text-gray-600">
                Leading security token platforms in Europe
              </p>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Strengths
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weaknesses
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {securityTokenPlatforms.map((platform, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <h3 className="text-blue-600 font-medium">{platform.name}</h3>
                          <p className="text-sm text-gray-600">{platform.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {platform.strengths.map((strength, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {platform.weaknesses.map((weakness, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <a
                          href={platform.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Visit
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Crypto Loan Platforms Section */}
        <section className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Crypto Loan Platforms
              </h2>
              <p className="mt-2 text-gray-600">
                Leading cryptocurrency lending platforms
              </p>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Strengths
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weaknesses
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loanPlatforms.map((platform, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <h3 className="text-blue-600 font-medium">{platform.name}</h3>
                          <p className="text-sm text-gray-600">{platform.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {platform.strengths.map((strength, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {platform.weaknesses.map((weakness, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <a
                          href={platform.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Visit
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Legal Advisory Firms Section */}
        <section className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Legal Advisory Firms
              </h2>
              <p className="mt-2 text-gray-600">
                Top legal firms specializing in cryptocurrency and blockchain law
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search legal firms..."
                className="pl-10 pr-4 py-2 bg-white/70 backdrop-blur border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64 transition-all duration-200 hover:bg-white"
              />
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th 
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-gray-400" />
                        Firm {getSortIcon('name')}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Strengths
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weaknesses
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedFirms.map((firm, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <h3 className="text-blue-600 font-medium">{firm.name}</h3>
                          <p className="text-sm text-gray-500">{firm.location}</p>
                          <p className="text-sm text-gray-600">{firm.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {firm.strengths.map((strength, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4">
                        <ul className="space-y-1">
                          {firm.weaknesses.map((weakness, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <a
                          href={firm.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Visit
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}